import { GRID_COLUMNS } from "src/cases/templates/caseBlocks/gridConstants";
import { CaseBlockConfigFormValue } from "src/cases/templates/formUtils/types";
import { CaseBlockPositioning } from "src/clients/case-management";

/**
 * Calculate the minimum number of rows to display in the grid.
 */
export const calculateMinRows = (
  blocks: CaseBlockConfigFormValue[],
  minEmptyRows = 2,
  absoluteMin = 8,
): number => {
  if (blocks.length === 0) {
    return absoluteMin;
  }

  const maxRowEnd = blocks
    // This is against types, but when react-hook-form handles
    // this data, sometimes while handling removing block
    // we get in situation when block.case_block_positioning is undefined
    // because block is being removed, this filter handles that situation
    .filter((block) => Boolean(block.case_block_positioning))
    .reduce((max, block) => {
      const pos = block.case_block_positioning;

      return Math.max(max, pos.row + pos.height - 1);
    }, 0);

  return Math.max(absoluteMin, maxRowEnd + minEmptyRows);
};

const DEFAULT_BLOCK_WIDTH = 3;
const DEFAULT_BLOCK_HEIGHT = 2;

/**
 * Check if a block can be placed at the given position without overlapping existing blocks.
 */
const canPlaceBlock = (
  blocks: CaseBlockConfigFormValue[],
  column: number,
  row: number,
  width: number,
  height: number,
): boolean => {
  const newColStart = column;
  const newColEnd = column + width - 1;
  const newRowStart = row;
  const newRowEnd = row + height - 1;

  for (const block of blocks) {
    const pos = block.case_block_positioning;
    const blockColStart = pos.column;
    const blockColEnd = pos.column + pos.width - 1;
    const blockRowStart = pos.row;
    const blockRowEnd = pos.row + pos.height - 1;

    const colOverlap = newColStart <= blockColEnd && newColEnd >= blockColStart;
    const rowOverlap = newRowStart <= blockRowEnd && newRowEnd >= blockRowStart;

    if (colOverlap && rowOverlap) {
      return false;
    }
  }
  return true;
};

/**
 * Calculate the position for a new block to be added to the grid.
 * First checks if there's available space on the right side of existing blocks,
 * otherwise places the block at the bottom of the grid.
 */
export const calculateNextBlockPosition = (
  blocks: CaseBlockConfigFormValue[],
): CaseBlockPositioning => {
  if (blocks.length === 0) {
    return {
      column: 1,
      row: 1,
      width: DEFAULT_BLOCK_WIDTH,
      height: DEFAULT_BLOCK_HEIGHT,
    };
  }

  const maxRowEnd = blocks.reduce((max, block) => {
    const pos = block.case_block_positioning;
    return Math.max(max, pos.row + pos.height - 1);
  }, 0);

  // Try to find space on the right side of the grid in the bottom rows
  // Check from the earliest row that could fit a block ending at maxRowEnd
  const earliestRow = Math.max(1, maxRowEnd - DEFAULT_BLOCK_HEIGHT + 1);

  for (let row = earliestRow; row <= maxRowEnd; row++) {
    // Check right side first (column 4 for a 3-wide block in a 6-column grid)
    const rightColumn = GRID_COLUMNS - DEFAULT_BLOCK_WIDTH + 1;
    if (
      canPlaceBlock(
        blocks,
        rightColumn,
        row,
        DEFAULT_BLOCK_WIDTH,
        DEFAULT_BLOCK_HEIGHT,
      )
    ) {
      return {
        column: rightColumn,
        row,
        width: DEFAULT_BLOCK_WIDTH,
        height: DEFAULT_BLOCK_HEIGHT,
      };
    }
  }

  // No space found on the right, place at the bottom
  return {
    column: 1,
    row: maxRowEnd + 1,
    width: DEFAULT_BLOCK_WIDTH,
    height: DEFAULT_BLOCK_HEIGHT,
  };
};
